import { useCallback, useMemo, useState } from "react";
import {
	SearchScope,
	useFederatedSearch,
} from "./useFederatedSearch";

/**
 * One search box across groups and people, filtered by the audience chips.
 *
 * Backed by My Pymble's federated search (see `useFederatedSearch`), so this
 * returns the same people in the same order as the portal navbar. The chips map
 * straight onto its scopes: one request, however many are ticked.
 *
 * The chips are a filter on one search rather than four separate pickers: the
 * person running this usually knows the name and not which category the API
 * files it under, and "is Lang H. a group or a class?" is not a question worth
 * making someone answer before they can type.
 *
 * ⚠️ PII. Results are names, year levels and group titles. Memory only, never
 * logged, cleared when the dialog closes.
 */

export type AudienceKey = "groups" | "students" | "staff" | "parents";

export const AUDIENCES: { key: AudienceKey; label: string }[] = [
	{ key: "groups", label: "Groups" },
	{ key: "students", label: "Students" },
	{ key: "staff", label: "Staff" },
	{ key: "parents", label: "Parents" },
];

/**
 * Chip to search scope. "Parents" is the word the school uses on screen;
 * `parent` is the scope name.
 */
const SCOPE: Record<AudienceKey, SearchScope> = {
	groups: "group",
	students: "student",
	staff: "staff",
	parents: "parent",
};

/**
 * Which pills start on: Groups, and nothing else.
 *
 * Attendees mostly arrive as a group — a year, a house, a class — so that is the
 * search worth having ready. Individual students, staff and carers are the
 * exception, and asking for one is a single click.
 *
 * Not all four: that returns a wall of near-duplicates for a common surname.
 * Not none either, which would leave Continue disabled the moment the dialog
 * opens. Declared once here because the initial value and `reset` must agree.
 */
const DEFAULT_AUDIENCES: AudienceKey[] = ["groups"];

/** A chosen invitee. `kind` decides which list it goes into on submit. */
export interface Invitee {
	id: string;
	name: string;
	detail: string;
	kind: "group" | "contact";
}

export function useInviteePicker() {
	const [audiences, setAudiences] = useState<Set<AudienceKey>>(
		() => new Set(DEFAULT_AUDIENCES)
	);
	const [picked, setPicked] = useState<Invitee[]>([]);

	const scopes = useMemo(
		() => AUDIENCES.filter((a) => audiences.has(a.key)).map((a) => SCOPE[a.key]),
		[audiences]
	);

	const search = useFederatedSearch(scopes);

	const toggleAudience = useCallback((key: AudienceKey) => {
		setAudiences((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const results: Invitee[] = useMemo(
		() =>
			search.hits.map((hit) => ({
				id: hit.id,
				name: hit.title,
				// The scope already labels a group in the UI, so its own subtitle
				// (a description) only earns the space when there is one.
				detail:
					hit.scope === "group"
						? hit.group_type_label || hit.subtitle || "Group"
						: hit.subtitle || "",
				kind: hit.scope === "group" ? ("group" as const) : ("contact" as const),
			})),
		[search.hits]
	);

	const pickedIds = useMemo(() => new Set(picked.map((p) => p.id)), [picked]);

	const toggle = useCallback((invitee: Invitee) => {
		setPicked((prev) =>
			prev.some((p) => p.id === invitee.id)
				? prev.filter((p) => p.id !== invitee.id)
				: prev.concat(invitee)
		);
	}, []);

	const remove = useCallback(
		(id: string) => setPicked((prev) => prev.filter((p) => p.id !== id)),
		[]
	);

	const { setTerm } = search;
	const reset = useCallback(() => {
		setPicked([]);
		setTerm("");
		setAudiences(new Set(DEFAULT_AUDIENCES));
	}, [setTerm]);

	return {
		/** False until a chip is on; the search cannot run without a scope. */
		hasAudience: audiences.size > 0,
		term: search.term,
		setTerm: search.setTerm,
		longEnough: search.longEnough,
		minQueryLength: search.minQueryLength,
		audiences,
		toggleAudience,
		results,
		searching: search.searching,
		searchFailed: search.searchFailed,
		picked,
		pickedIds,
		toggle,
		remove,
		reset,
		groupIds: useMemo(
			() => picked.filter((p) => p.kind === "group").map((p) => p.id),
			[picked]
		),
		contactIds: useMemo(
			() => picked.filter((p) => p.kind === "contact").map((p) => p.id),
			[picked]
		),
	};
}
